import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { supabase } from '@/lib/supabase';

const FORM_URL = 'https://readdy.ai/api/form/d96gel70d76aer3t54b0';

const ContattiSection = () => {
  const { t } = useTranslation();
  const left = useScrollAnimation();
  const right = useScrollAnimation({ threshold: 0.1 });

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (message.length > 500) return;
    setStatus('loading');
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      form.reset();
      setMessage('');
      return;
    }
    formData.delete('phone_alt');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* non-JSON response */ }
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
        setMessage('');

        // Invia notifica email via Edge Function (fire-and-forget)
        supabase.functions.invoke('send-contact-notification', {
          body: {
            nome: formData.get('name'),
            email: formData.get('email'),
            telefono: formData.get('phone'),
            messaggio: formData.get('message'),
          },
        }).catch(() => {});
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClass =
    'w-full font-sans text-sm bg-ivory-100 border-0 rounded-lg px-6 py-4 text-anthracite-700 placeholder-anthracite-300 focus:outline-none focus:ring-2 focus:ring-terracotta-200 transition-all duration-300';

  return (
    <section id="contatti" className="bg-white py-28 md:py-36 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-stretch">

          {/* Left: info */}
          <div
            ref={left.ref}
            className={`fade-in-up ${left.isVisible ? 'visible' : ''}`}
          >
            <p className="font-sans text-xs tracking-widest2 uppercase text-terracotta-300 mb-6">
              {t('contatti.overline')}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-anthracite-800 font-light leading-tight mb-12">
              {t('contatti.title').split('\n').map((line, i, arr) => (
                <span key={i}>
                  {line}
                  {i < arr.length - 1 && <br />}
                </span>
              ))}
            </h2>

            {/* Contact details */}
            <div className="flex flex-col gap-6">

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full border border-ivory-200">
                  <i className="ri-instagram-line text-terracotta-300"></i>
                </div>
                <div>
                  <p className="font-sans text-xs uppercase tracking-widest text-anthracite-300 mb-0.5">Instagram</p>
                  <a
                    href="https://www.instagram.com/felicity.dinallo_/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="font-sans text-sm text-terracotta-300 hover:text-terracotta-400 transition-colors cursor-pointer"
                  >
                    {t('contatti.instagram')}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full border border-ivory-200">
                  <i className="ri-translate-2 text-terracotta-300"></i>
                </div>
                <div>
                  <p className="font-sans text-xs uppercase tracking-widest text-anthracite-300 mb-0.5">Lingue</p>
                  <p className="font-sans text-sm text-anthracite-600">
                    🇮🇹 🇬🇧 🇪🇸 &nbsp;{t('contatti.lingue')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div
            ref={right.ref}
            className={`fade-in-up stagger-2 self-end ${right.isVisible ? 'visible' : ''}`}
          >
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-olive-50 border border-olive-200 mb-6">
                  <i className="ri-check-line text-2xl text-olive-400"></i>
                </div>
                <h3 className="font-serif text-3xl text-anthracite-800 mb-3">{t('contatti.success')}</h3>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-4 font-sans text-sm text-terracotta-300 hover:text-terracotta-400 underline cursor-pointer"
                >
                  Invia un altro messaggio
                </button>
              </div>
            ) : (
              <>
                <p className="font-sans text-base font-bold text-anthracite-500 mb-4">
                  Compila il form e verrai subito ricontattato/a
                </p>
                <form
                  onSubmit={handleSubmit}
                  id="contatti-marche-casa"
                  className="flex flex-col gap-5"
                  data-readdy-form=""
                >
                {/* Honeypot anti-spam */}
                <input type="text" name="phone_alt" className="form-phone-alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                <input
                  type="text"
                  name="name"
                  required
                  placeholder={t('contatti.name')}
                  className={inputClass}
                />
                <input
                  type="email"
                  name="email"
                  required
                  placeholder={t('contatti.email')}
                  className={inputClass}
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder={t('contatti.phone')}
                  className={inputClass}
                />
                <div className="relative">
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t('contatti.message.placeholder')}
                    className={`${inputClass} resize-none`}
                  />
                  <span className={`absolute bottom-3 right-4 font-sans text-xs ${message.length > 480 ? 'text-terracotta-300' : 'text-anthracite-300'}`}>
                    {message.length}/500
                  </span>
                </div>

                {status === 'error' && (
                  <p className="font-sans text-sm text-terracotta-400">{t('contatti.error')}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading' || message.length > 500}
                  className="w-full py-5 bg-terracotta-300 text-white font-sans text-sm tracking-widest uppercase rounded-full hover:bg-terracotta-400 transition-all duration-300 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {status === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Invio...
                    </span>
                  ) : (
                    t('contatti.submit')
                  )}
                </button>
              </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContattiSection;